import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRegistryPerms } from '../lib/perms'
import { Plus, Trash2, Pencil, ClipboardList } from 'lucide-react'
import Drawer from './Drawer'
import ModelWizard from './ModelWizard'
import { useConfirm, deleteJSON } from './ConfirmDialog'
import { ErrorState } from './ViewState'
import { useWS } from '../contexts/WSContext'
import { ICONS, EXODIA_IMAGES, EXODIA_KEYS, SEVERITIES, iconOf } from '../equipment'
import { apiError } from '../lib/api'

function EquipIcon({ svg, className }) {
  return (
    <span
      className={`equip-icon block ${className}`}
      style={{ WebkitMaskImage: `url(/equipments/${svg}.svg)`, maskImage: `url(/equipments/${svg}.svg)` }}
    />
  )
}

const EMPTY = { name: '', label: ICONS[0], manufacturer: '', model: '', description: '', checklist: [], images: [], cycle_steps: [] }

// cycle_steps é uma lista de {code, label}; o formato antigo (lista de strings) entra
// como rótulo sem código, e o backend deriva o código pela posição na grade do tipo.
const toSteps = (raw) => (Array.isArray(raw) ? raw : []).map(st =>
  typeof st === 'string' ? { code: '', label: st } : { code: st?.code || '', label: st?.label || '' })

// Prévia das fotos do checklist vinculado: poucas miniaturas para o card não crescer.
function ChecklistThumbs({ checklist }) {
  const items = (checklist?.items || []).filter(it => it.image_id)
  if (!items.length) return null
  const shown = items.slice(0, 6)
  const rest = items.length - shown.length
  return (
    <div className="mt-2 flex items-center gap-1">
      {shown.map(it => (
        <img key={it.code} src={`/api/images/${it.image_id}`} alt="" title={it.texts?.en || it.text || it.code}
          className="w-9 h-7 object-cover rounded border border-slate-200 dark:border-slate-700" />
      ))}
      {rest > 0 && <span className="text-[11px] text-slate-400 dark:text-slate-500">+{rest}</span>}
    </div>
  )
}

export default function AdminFleet() {
  const { t } = useTranslation()
  const { mayEdit, mayDelete, reasonEdit, reasonDelete } = useRegistryPerms()
  const { categories, models, checklists, adminError, reloadAdmin } = useWS()
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [modelOpen, setModelOpen] = useState(false)
  const [editingModel, setEditingModel] = useState(null)
  const [confirmDialog, ask] = useConfirm()

  const openCreateModel = () => { setEditingModel(null); setModelOpen(true) }
  const openEditModel = (m) => { setEditingModel(m); setModelOpen(true) }
  const removeModel = (m) => ask({
    title: t('admin:fleet.delete_vehicle_title'),
    message: t('admin:fleet.delete_vehicle_message', { name: m.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/equipment-models/${m.id}`); reloadAdmin() },
  })

  const openCreate = () => { setEditingId(null); setForm(EMPTY); setError(null); setOpen(true) }
  const openEdit = (c) => {
    setEditingId(c.id)
    setForm({
      name: c.name || '', label: c.label || ICONS[0], manufacturer: c.manufacturer || '', model: c.model || '',
      description: c.description || '',
      checklist: Array.isArray(c.checklist) ? c.checklist : [],
      images: Array.isArray(c.images) ? c.images : [],
      cycle_steps: toSteps(c.cycle_steps),
    })
    setError(null); setOpen(true)
  }

  const save = async (e) => {
    e.preventDefault()
    setSaving(true); setError(null)
    try {
      const url = editingId ? `/api/equipment-categories/${editingId}` : '/api/equipment-categories'
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) })
      if (!res.ok) throw await apiError(res)
      setOpen(false); reloadAdmin()
    } catch (err) { setError(err.message) } finally { setSaving(false) }
  }

  // O tipo é o que resolve ícone, passos do ciclo e checklist dos equipamentos
  // adotados nele: apagar reflete em toda máquina daquele tipo.
  const remove = (c) => ask({
    title: t('admin:fleet.delete_type_title'),
    message: t('admin:fleet.delete_type_message', { name: c.name }),
    confirmLabel: t('common:actions.delete'),
    danger: true,
    onConfirm: async () => { await deleteJSON(`/api/equipment-categories/${c.id}`); reloadAdmin() },
  })

  const addItem = () => setForm(f => ({ ...f, checklist: [...f.checklist, { item: '', severity: 'C' }] }))
  const setItem = (i, patch) => setForm(f => ({ ...f, checklist: f.checklist.map((it, idx) => idx === i ? { ...it, ...patch } : it) }))
  const delItem = (i) => setForm(f => ({ ...f, checklist: f.checklist.filter((_, idx) => idx !== i) }))
  const toggleImg = (k) => setForm(f => ({ ...f, images: f.images.includes(k) ? f.images.filter(x => x !== k) : [...f.images, k] }))

  const addStep = () => setForm(f => ({ ...f, cycle_steps: [...f.cycle_steps, { code: '', label: '' }] }))
  const setStep = (i, label) => setForm(f => ({ ...f, cycle_steps: f.cycle_steps.map((s, idx) => idx === i ? { ...s, label } : s) }))
  const delStep = (i) => setForm(f => ({ ...f, cycle_steps: f.cycle_steps.filter((_, idx) => idx !== i) }))

  return (
    <div className="h-full overflow-y-auto p-6 scrollbar-thin">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:fleet.vehicle_count', { count: models.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreateModel} className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-brand text-white hover:bg-brand-700 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={16} /> {t('admin:fleet.add_vehicle')}
        </button>
      </div>

      {adminError ? (
        <ErrorState message={adminError} onRetry={reloadAdmin} className="h-36 mb-8" />
      ) : models.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreateModel} className="w-full flex flex-col items-center justify-center gap-2 h-36 mb-8 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={24} />
          <span className="text-sm">{t('admin:fleet.empty_vehicles_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 mb-8">
          {models.map(m => {
            const imgs = m.images && typeof m.images === 'object' ? Object.entries(m.images) : []
            return (
              <div key={m.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
                    <EquipIcon svg={iconOf(m.category_label)} className="w-7 h-7" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{m.name}</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{[m.manufacturer, m.category_name].filter(Boolean).join(' · ')}</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button disabled={!mayEdit} onClick={() => openEditModel(m)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                    <button disabled={!mayDelete} onClick={() => removeModel(m)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                  </div>
                </div>
                {m.checklist_name && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
                    <ClipboardList size={12} className="text-slate-400 dark:text-slate-500" /> {m.checklist_name}
                  </p>
                )}
                {imgs.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {imgs.slice(0, 5).map(([slot, id]) => (
                      <img key={slot} src={`/api/images/${id}`} alt={slot} title={slot} className="w-10 h-8 object-cover rounded" />
                    ))}
                  </div>
                )}
                <ChecklistThumbs checklist={checklists.find(c => c.id === m.checklist_id)} />
              </div>
            )
          })}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">{t('admin:fleet.type_count', { count: categories.length })}</p>
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={15} /> {t('admin:fleet.new_type')}
        </button>
      </div>

      {categories.length === 0 ? (
        <button disabled={!mayEdit} title={!mayEdit ? t(reasonEdit) : undefined} onClick={openCreate} className="w-full flex flex-col items-center justify-center gap-2 h-48 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:border-brand/50 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed">
          <Plus size={28} />
          <span className="text-sm">{t('admin:fleet.empty_types_cta')}</span>
        </button>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {categories.map(c => (
            <div key={c.id} className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 p-4 hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 shrink-0">
                  <EquipIcon svg={iconOf(c.label)} className="w-7 h-7" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 dark:text-slate-100 text-sm truncate">{c.name}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{[c.manufacturer, c.model].filter(Boolean).join(' · ') || c.description || c.label}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button disabled={!mayEdit} onClick={() => openEdit(c)} title={!mayEdit ? t(reasonEdit) : t('common:actions.edit')} className="text-slate-300 dark:text-slate-600 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"><Pencil size={15} /></button>
                  <button disabled={!mayDelete} onClick={() => remove(c)} title={!mayDelete ? t(reasonDelete) : t('admin:actions.remove')} className="text-slate-300 dark:text-slate-600 hover:text-status-down disabled:opacity-40 disabled:cursor-not-allowed"><Trash2 size={15} /></button>
                </div>
              </div>
              {Array.isArray(c.checklist) && c.checklist.length > 0 && (
                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{t('admin:fleet.checklist_items', { count: c.checklist.length })}</p>
              )}
              {Array.isArray(c.images) && c.images.length > 0 && (
                <div className="mt-2 flex gap-1">
                  {c.images.slice(0, 5).map(k => EXODIA_IMAGES[k] && <img key={k} src={EXODIA_IMAGES[k]} alt={k} className="w-8 h-11 object-cover rounded" />)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        title={editingId ? t('admin:fleet.edit_machine') : t('admin:fleet.new_machine')}
        subtitle={t('admin:fleet.machine_subtitle')}
        footer={
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="text-sm px-3 py-1.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">{t('common:actions.cancel')}</button>
            <button type="submit" form="fleet-form" disabled={saving} className="text-sm font-semibold px-4 py-1.5 rounded-md bg-brand text-white hover:bg-brand-700 disabled:bg-slate-200 disabled:text-slate-400">
              {saving ? t('admin:actions.saving') : editingId ? t('common:actions.save') : t('admin:actions.register')}
            </button>
          </div>
        }
      >
        <form id="fleet-form" onSubmit={save} className="px-5 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block col-span-2"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.name')}</span>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder={t('admin:fleet.fields.name_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>
            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.manufacturer')}</span>
              <input value={form.manufacturer} onChange={e => setForm({ ...form, manufacturer: e.target.value })} placeholder={t('admin:fleet.fields.manufacturer_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>
            <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.model')}</span>
              <input value={form.model} onChange={e => setForm({ ...form, model: e.target.value })} placeholder={t('admin:fleet.fields.model_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>
          </div>

          <div><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.icon')}</span>
            <div className="mt-1 grid grid-cols-5 gap-2">
              {ICONS.map(ic => (
                <button type="button" key={ic} onClick={() => setForm({ ...form, label: ic })} title={ic}
                  className={`aspect-square rounded-lg border flex items-center justify-center ${form.label === ic ? 'border-brand bg-brand/5 text-brand-600' : 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-500 hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                  <EquipIcon svg={ic} className="w-6 h-6" />
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.checklist')}</span>
              <button type="button" onClick={addItem} className="text-xs text-brand-600 hover:text-brand-700 inline-flex items-center gap-1"><Plus size={12} /> {t('admin:fleet.fields.add_item')}</button>
            </div>
            <div className="mt-1 space-y-2">
              {form.checklist.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">{t('admin:fleet.fields.checklist_hint')}</p>}
              {form.checklist.map((it, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input value={it.item} onChange={e => setItem(i, { item: e.target.value })} placeholder={t('admin:fleet.fields.item_ph')} className="flex-1 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
                  <select value={it.severity} onChange={e => setItem(i, { severity: e.target.value })} title={t('admin:fleet.fields.severity')} className="text-xs border border-slate-200 rounded-md px-1.5 py-1.5 bg-white text-slate-600 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100">
                    {SEVERITIES.map(s => <option key={s.value} value={s.value}>{s.value}</option>)}
                  </select>
                  <button type="button" onClick={() => delItem(i)} className="text-slate-300 dark:text-slate-600 hover:text-status-down"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:fleet.fields.severity_help')}</p>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.cycle_steps')}</span>
              <button type="button" onClick={addStep} className="text-xs text-brand-600 hover:text-brand-700 inline-flex items-center gap-1"><Plus size={12} /> {t('admin:fleet.fields.add_step')}</button>
            </div>
            <div className="mt-1 space-y-2">
              {form.cycle_steps.length === 0 && <p className="text-xs text-slate-400 dark:text-slate-500">{t('admin:fleet.fields.cycle_steps_hint')}</p>}
              {form.cycle_steps.map((st, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 dark:text-slate-500 w-5 text-right tabular-nums">{i + 1}</span>
                  <input value={st.label} onChange={e => setStep(i, e.target.value)} placeholder={t('admin:fleet.fields.step_ph')} className="flex-1 text-sm border border-slate-200 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" />
                  <span title={t('admin:fleet.fields.step_code')} className={`text-[11px] font-mono px-1.5 py-1 rounded bg-slate-100 dark:bg-slate-800 ${st.code ? 'text-slate-500 dark:text-slate-400' : 'text-slate-300 dark:text-slate-600 italic'}`}>
                    {st.code || t('admin:fleet.fields.step_code_auto')}
                  </span>
                  <button type="button" onClick={() => delStep(i)} className="text-slate-300 dark:text-slate-600 hover:text-status-down"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
            <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{t('admin:fleet.fields.cycle_steps_help')}</p>
          </div>

          <div><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.images')} <span className="text-slate-300 dark:text-slate-600">{t('admin:fleet.fields.images_placeholder')}</span></span>
            <div className="mt-1 grid grid-cols-5 gap-2">
              {EXODIA_KEYS.map(k => (
                <button type="button" key={k} onClick={() => toggleImg(k)} title={k}
                  className={`aspect-[2/3] rounded-md overflow-hidden border-2 ${form.images.includes(k) ? 'border-brand' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                  <img src={EXODIA_IMAGES[k]} alt={k} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <label className="block"><span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('admin:fleet.fields.description')}</span>
            <input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder={t('admin:fleet.fields.optional_ph')} className="mt-1 w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-100 dark:placeholder-slate-500" /></label>

          {error && <p className="text-xs text-red-600">{error}</p>}
        </form>
      </Drawer>

      <ModelWizard key={`model-${editingModel?.id ?? 'new'}-${modelOpen}`} open={modelOpen} onClose={() => setModelOpen(false)} editing={editingModel} />
      {confirmDialog}
    </div>
  )
}
